/**
 * RESPONSIBILITY: draw the /adc page's blocks in order, each as its template.
 *
 * A lookup and not a `switch`: a new template is a key here and a file beside it. A block
 * whose template this build has never heard of draws nothing rather than throwing — that
 * is a deployment mid-flight, not a reason to show a visitor a blank page.
 *
 * A block switched off is not drawn, for anybody; the switch is a row of the list the
 * pencil opens, so the person who turned it off can turn it back on.
 */
import { idOf } from '@framework/core';
import { ADC_THEME as T, type AdcBlockRow } from '@app/sections';
import { isShown } from '../commons/shown';
import { AdcImage } from './AdcImage';
import { AdcText } from './AdcText';
import { AdcImageLeft, AdcImageRight } from './AdcSplit';
import { AdcImageTop, AdcTextTop } from './AdcStacked';
import { AdcFaq } from './AdcFaq';
import { AdcVideo } from './AdcVideo';
import { AdcGallery } from './AdcGallery';
import type { AdcBlockProps } from './kind';

const BLOCKS: Record<string, (p: AdcBlockProps) => JSX.Element | null> = {
  [T.Image]: AdcImage,
  [T.Text]: AdcText,
  [T.ImageLeft]: AdcImageLeft,
  [T.ImageRight]: AdcImageRight,
  [T.ImageTop]: AdcImageTop,
  [T.TextTop]: AdcTextTop,
  [T.Faq]: AdcFaq,
  [T.Video]: AdcVideo,
  [T.Gallery]: AdcGallery,
};

export function AdcBlocks({ blocks }: { blocks: readonly AdcBlockRow[] | undefined }) {
  return (
    <>
      {(blocks ?? []).filter(isShown).map((b) => {
        const Draw = BLOCKS[b.theme];
        return Draw ? <Draw key={idOf(b)} b={b} /> : null;
      })}
    </>
  );
}
